import { A, D, flow } from '@mobily/ts-belt'
import { type TvChannelWithEpg } from '@setplex/tria-api'
import { combine, createStore, sample, Store } from 'effector'
import { features } from '~/core/flags'
import { EpgDateEx, epgDatesModel, isSameDate } from '~/entities/epg/dates'
import { epgProgramsModel } from '~/entities/epg/programs'
import { selectEpgChannelModel } from '~/features/epg/channels/select'
import { selectEpgDateModel } from '~/features/epg/dates'
import {
  epgDateToRange,
  formatProgramToView,
  type LiveProgramInfo,
} from '~/features/epg/programs'
import { $route } from '~/processes/navigation'
import { model as clock } from '~/shared/clock'
import { ONE_MINUTE_MS } from '~/shared/constants'
import { extendWithProgram } from '~/shared/helpers/epg'
import { $activeList } from '../channels/model'
import { filterProgramsByEpgSettings, setDayProgram } from './helpers'
import { formatProgramToLocalizedView } from './lib'

// currently active category channels merged with their epg data
const $activeChannelsListWithEPG: Store<readonly TvChannelWithEpg[]> = combine(
  {
    channels: $activeList,
    epg: epgProgramsModel.$epg,
    today: epgDatesModel.$today,
    epgSettings: features.$epgSettings,
  },
  ({ channels, epg: [programs], today, epgSettings }) => {
    const { pastHours, futureHours } = epgSettings || {}
    const period = epgDateToRange(today, pastHours, futureHours)

    return A.map(channels, extendWithProgram(programs, period))
  }
)

const $activeChannelsListWithFormattedEPG = combine(
  {
    channels: $activeChannelsListWithEPG,
    now: clock.$minutes,
  },
  ({ channels, now }) => {
    return channels.map(
      flow(
        D.updateUnsafe(
          'programs',
          A.map(
            flow(
              formatProgramToView({ now }),
              formatProgramToLocalizedView({ now })
            )
          )
        )
      )
    )
  }
)

// Channels list by active category with programs for player sidebar
export const $activeChannelsListWithLive =
  $activeChannelsListWithFormattedEPG.map((channels) =>
    channels.map(
      flow(
        D.updateUnsafe(
          'programs',
          A.filter((program) => program.isLive)
        )
      )
    )
  )

// Handle programs from player playlist
// store only for days that have epg
export const $dayTabsWithProgram = createStore<
  null | { date: EpgDateEx; tvProgram: LiveProgramInfo[] }[]
>(null)

// Active channel with programs
export const $activeChannelWithFormattedEPG: Store<TvChannelWithEpg | null> =
  combine(
    {
      activeChannel: selectEpgChannelModel.$active,
      epg: epgProgramsModel.$epg,
      now: clock.$minutes,
      catchupEnabled: features.$catchupEnabled,
      epgSettings: features.$epgSettings,
      today: epgDatesModel.$today,
    },
    ({
      activeChannel,
      epg: [programs],
      now,
      catchupEnabled,
      epgSettings,
      today,
    }) => {
      if (!activeChannel) return null
      const { pastHours, futureHours } = epgSettings || {}
      const period = epgDateToRange(today, pastHours, futureHours)
      const activeChannelWithEPG = extendWithProgram(
        programs,
        period,
        catchupEnabled
      )(activeChannel)

      return D.updateUnsafe(
        activeChannelWithEPG,
        'programs',
        A.map(
          flow(
            formatProgramToView({ now }),
            formatProgramToLocalizedView({ now })
          )
        )
      )
    }
  )

// set available tabs for current channel only with epg
sample({
  clock: [
    $activeChannelWithFormattedEPG,
    epgDatesModel.$week,
    selectEpgChannelModel.$active,
    $route,
  ],
  source: {
    activeChannel: $activeChannelWithFormattedEPG,
    tabs: epgDatesModel.$week,
    channel: selectEpgChannelModel.$active,
    route: $route,
    epgSettings: features.$epgSettings,
  },
  fn: ({ activeChannel, tabs, channel, epgSettings }) => {
    if (!tabs || !activeChannel || !channel) return null

    const weekTabs = tabs

    const { enabled: isTimeshiftEnabled, shift = 0 } =
      activeChannel?.timeshift || {}
    let timeshiftTimeMs = 0
    if (isTimeshiftEnabled) {
      const userTimezoneOffset = -new Date().getTimezoneOffset()
      timeshiftTimeMs = (shift - userTimezoneOffset) * ONE_MINUTE_MS
    }

    const programList = filterProgramsByEpgSettings(
      activeChannel?.programs || [],
      epgSettings,
      timeshiftTimeMs
    )

    // set empty [] if programs isEmpty for all the period
    const isAllEmpty = programList.every(({ isEmpty }) => isEmpty)
    if (isAllEmpty) return []

    const list = weekTabs.map((day) => {
      return { date: day, tvProgram: setDayProgram(day, programList) }
    })
    // filter only days with epg data
    return list
      .filter(
        (item) => !(item.tvProgram.length === 1 && item.tvProgram[0].isEmpty)
      )
      .filter((item) => Boolean(item.tvProgram.length))
  },
  target: $dayTabsWithProgram,
})

// store for tabs in epg sidebar
export const $dayTabs = $dayTabsWithProgram.map((dayTabsWithProgram) => {
  if (!dayTabsWithProgram) return null
  return dayTabsWithProgram.map((item) => item.date)
})

// set new active day after day change
sample({
  clock: epgDatesModel.$today,
  source: {
    active: selectEpgDateModel.$active,
    dayTabs: $dayTabs,
  },
  // if today was updated, and active day disappeared from the week
  filter: ({ active, dayTabs }) =>
    active != null &&
    dayTabs !== null &&
    dayTabs.find(isSameDate(active)) == null,
  // -> select firts available day
  fn: ({ dayTabs }) => dayTabs?.[0],
  target: selectEpgDateModel.setActive,
})

export const $tvProgramList = combine(
  {
    dayTabsWithProgram: $dayTabsWithProgram,
    activeDay: selectEpgDateModel.$active,
  },
  ({ dayTabsWithProgram, activeDay }) => {
    if (!dayTabsWithProgram || !activeDay) return null
    return (
      dayTabsWithProgram.find((item) => item.date === activeDay)?.tvProgram ||
      []
    )
  }
)
